import axios from "axios";
import { getEnv } from "../config/getEnv";
import { storage } from "../auth/storage";

// Salesforce Apex REST connector using axios
// Environment variables:
// - REACT_APP_SF_BASE_URL: e.g. https://your-instance.my.salesforce.com
// - REACT_APP_SF_APEX_PREFIX: defaults to '/services/apexrest'
// - REACT_APP_SF_WITH_CREDENTIALS: 'true' to send cookies (optional)

const DEFAULT_APEX_PREFIX = "/services/apexrest";

function joinUrl(base, path) {
  if (!base) return path || "";
  const b = base.endsWith("/") ? base.slice(0, -1) : base;
  if (!path) return b;
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${b}${p}`;
}

function stripPrefix(value, prefix) {
  if (!value) return "";
  if (value.startsWith(prefix)) return value.slice(prefix.length);
  return value;
}

function defaultSfAccessToken() {
  if (typeof window === "undefined") return null;
  try {
    return storage.readSfAuthSession()?.tokenSet?.access_token ?? null;
  } catch {
    return null;
  }
}

let authTokenProvider = defaultSfAccessToken;
let unauthorizedHandler = null; // (error) => void

export function setAuthTokenProvider(providerFn) {
  if (typeof providerFn === "function") authTokenProvider = providerFn;
}

export function setUnauthorizedHandler(handlerFn) {
  if (typeof handlerFn === "function") unauthorizedHandler = handlerFn;
}

export function createSalesforceClient(options = {}) {
  const {
    baseUrl = getEnv("REACT_APP_SF_BASE_URL") || "",
    apexPrefix = getEnv("REACT_APP_SF_APEX_PREFIX") || DEFAULT_APEX_PREFIX,
    withCredentials = (getEnv("REACT_APP_SF_WITH_CREDENTIALS") || "false") ===
      "true",
    timeoutMs = 15000,
  } = options;

  const baseURL = joinUrl(baseUrl, apexPrefix);

  const instance = axios.create({
    baseURL,
    timeout: timeoutMs,
    withCredentials,
    // Accept both 200 (OK) and 201 (Created) as successful responses
    validateStatus: (status) => status >= 200 && status < 300,
  });

  instance.interceptors.request.use(
    async (config) => {
      const token = await Promise.resolve(authTokenProvider());
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Default JSON headers if sending a body
      if (config.data && !config.headers["Content-Type"]) {
        config.headers["Content-Type"] = "application/json";
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  instance.interceptors.response.use(
    (response) => {
      // HTTP 201 (Created) is a successful response for create operations
      // HTTP 200 (OK) is a successful response for all operations
      const isSuccessStatus = response.status >= 200 && response.status < 300;

      console.log("🔍 [salesforceApi] Response interceptor:", {
        status: response.status,
        url: response.config?.url,
        method: response.config?.method,
        hasData: !!response.data,
        dataType: typeof response.data,
        isArray: Array.isArray(response.data),
      });

      // Unwrap common envelope: { success, status, message, error, data }
      const body = response?.data;
      if (
        body &&
        typeof body === "object" &&
        Object.prototype.hasOwnProperty.call(body, "success")
      ) {
        console.log("🔍 [salesforceApi] Found success field:", body.success);
        if (body.success === true) {
          // Replace response data with inner payload when present.
          // Support both legacy `data` and new `listData` envelopes.
          if (
            Object.prototype.hasOwnProperty.call(body, "listData") ||
            Object.prototype.hasOwnProperty.call(body, "data")
          ) {
            // Prefer non-null listData when provided; otherwise fall back to data
            const unwrapped = body.listData ?? body.data;
            console.log("🔍 [salesforceApi] Unwrapping envelope, new data:", {
              type: typeof unwrapped,
              isArray: Array.isArray(unwrapped),
              hasId:
                unwrapped?.Id || (Array.isArray(unwrapped) && unwrapped[0]?.Id),
            });
            response.data = unwrapped;
          }
          return response;
        }
        // success === false: raise a normalized error even if HTTP 200/201
        console.error("🔍 [salesforceApi] Success field is false, rejecting");
        const message =
          body.message ||
          (typeof body.error === "string" ? body.error : body.error?.message) ||
          "Salesforce error";
        const err = new SalesforceError(message, {
          status: body.status || response.status,
          code: body.error?.code || "APEX_ERROR",
          details: body,
          response,
          url: response?.config?.url,
          method: response?.config?.method,
        });
        return Promise.reject(err);
      }

      // If no success field but status is 2xx (including 201), treat as success
      console.log(
        "🔍 [salesforceApi] No success field, status:",
        response.status,
      );
      if (isSuccessStatus) {
        console.log("🔍 [salesforceApi] Treating as success (2xx status)");
        return response;
      }

      return response;
    },
    async (error) => {
      const normalized = normalizeSalesforceAxiosError(error);
      if (
        normalized.status === 401 &&
        typeof unauthorizedHandler === "function"
      ) {
        try {
          await Promise.resolve(unauthorizedHandler(normalized));
        } catch (e) {
          // swallow handler errors; original error continues
        }
      }
      return Promise.reject(normalized);
    },
  );

  return instance;
}

// Default singleton client
export const sfClient = createSalesforceClient();

export class SalesforceError extends Error {
  constructor(message, { status, code, details, response, url, method } = {}) {
    super(message || "Salesforce request failed");
    this.name = "SalesforceError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.response = response;
    this.url = url;
    this.method = method;
  }
}

function normalizeSalesforceAxiosError(error) {
  // Axios network error (no response)
  if (!error || !error.response) {
    const message = error?.message || "Network error";
    return new SalesforceError(message, {
      status: 0,
      code: "NETWORK_ERROR",
      details: null,
      response: null,
      url: error?.config?.url,
      method: error?.config?.method,
    });
  }

  const { status, data, config } = error.response;
  const parsed = parseSalesforceErrorPayload(data);
  const message = parsed.message || error.message || `HTTP ${status}`;
  return new SalesforceError(message, {
    status,
    code: parsed.code,
    details: parsed.details,
    response: error.response,
    url: config?.url,
    method: config?.method,
  });
}

function parseSalesforceErrorPayload(data) {
  // Common shapes from Apex REST:
  // 1) [{ message: string, errorCode: string, fields?: string[] }]
  if (Array.isArray(data) && data.length > 0 && data[0]) {
    const first = data[0];
    if (first.message || first.errorCode) {
      return {
        message: first.message || "Salesforce error",
        code: first.errorCode || "APEX_ERROR",
        details: data,
      };
    }
  }
  // 2) { message: string, errorCode: string }
  if (data && typeof data === "object") {
    if (data.message || data.errorCode) {
      return {
        message: data.message || "Salesforce error",
        code: data.errorCode || "APEX_ERROR",
        details: data,
      };
    }
    // 3) { success: false, errors: [...] }
    if (data.success === false && Array.isArray(data.errors)) {
      const first = data.errors[0] || {};
      return {
        message: first.message || "Salesforce error",
        code: first.errorCode || "APEX_ERROR",
        details: data.errors,
      };
    }
  }
  // Fallback
  return { message: "Salesforce error", code: "APEX_ERROR", details: data };
}

function toRelativeApexPath(path, apexPrefix = DEFAULT_APEX_PREFIX) {
  if (!path) return "";
  const withoutPrefix = stripPrefix(path, apexPrefix);
  const trimmed = withoutPrefix.startsWith("/")
    ? withoutPrefix.slice(1)
    : withoutPrefix;
  return trimmed;
}

async function request(method, path, { params, data, config } = {}) {
  const relative = toRelativeApexPath(
    path,
    getEnv("REACT_APP_SF_APEX_PREFIX") || DEFAULT_APEX_PREFIX,
  );
  try {
    const resp = await sfClient.request({
      method,
      url: relative,
      params,
      data,
      ...(config || {}),
    });
    return resp.data;
  } catch (err) {
    // err is normalized SalesforceError — surface to callers
    throw err;
  }
}

export function sfGet(path, params, config) {
  return request("get", path, { params, config });
}

export function sfPost(path, data, config) {
  return request("post", path, { data, config });
}

export function sfPut(path, data, config) {
  return request("put", path, { data, config });
}

export function sfPatch(path, data, config) {
  return request("patch", path, { data, config });
}

export function sfDelete(path, params, config) {
  return request("delete", path, { params, config });
}

// User Search API
export async function searchUsersByEmail(email) {
  const response = await sfGet("/api/users/search", { email });
  // The API returns { success, data, count }
  // Extract and normalize the user data
  if (response && Array.isArray(response)) {
    // If response is already the array (after envelope unwrapping)
    return response.map((user) => ({
      Id: user.id || user.Id,
      Name: user.name || user.Name,
      Email: user.email || user.Email,
      Username: user.username || user.Username,
      IsActive: user.isActive || user.IsActive,
      ProfileName: user.profileName || user.ProfileName,
    }));
  }
  return [];
}

export async function searchAccountByName(searchKey) {
  const response = await sfGet("/api/account/search", { account: searchKey });
  const list = response?.data || response;
  if (Array.isArray(list)) {
    return list.map((account) => ({
      Id: account.id,
      Name: account.name,
    }));
  }
  return [];
}

export async function searchContributorByName(searchKey) {
  const response = await sfGet("/api/contributor/search", {
    contributor: searchKey,
  });
  const list = response?.data || response;
  if (Array.isArray(list)) {
    return list.map((contributor) => ({
      Id: contributor.id,
      Name: contributor.name,
    }));
  }
  return [];
}

// Project Search API
export async function searchProjectsByName(searchKey) {
  const response = await sfGet("/Project", { searchkey: searchKey });
  // The API returns an array of projects with Id and Name
  if (response && Array.isArray(response)) {
    return response.map((project) => ({
      Id: project.Id,
      Name: project.Name,
    }));
  }
  return [];
}

// Project Objective Search API
export async function searchProjectObjectivesByName(searchKey, projectId) {
  const response = await sfGet("/ProjectObjective", {
    searchkey: searchKey,
    pid: projectId,
  });
  // The API returns an array of project objectives with Id and Name
  if (response && Array.isArray(response)) {
    return response.map((objective) => ({
      Id: objective.Id,
      Name: objective.Name,
      ProjectName: objective.Project__r?.Name || "",
    }));
  }
  return [];
}

// Project Page Search API
export async function searchProjectPagesByName(searchKey) {
  const response = await sfGet("/ProjectPage", { searchkey: searchKey });
  // The API returns an array of project pages with Id, Name, and Project_Page_Type__c
  if (response && Array.isArray(response)) {
    return response.map((page) => ({
      Id: page.Id,
      Name: page.Name,
      ProjectName: page.Project__r?.Name || "",
      PageType: page.Project_Page_Type__c || "",
    }));
  }
  return [];
}

// Project Qualification Step Search API
export async function searchProjectQualStepsByName(
  searchKey,
  projectId,
  projectObjectiveId,
) {
  const response = await sfGet("/ProjectQualStep", {
    searchkey: searchKey,
    pid: projectId || "",
    poid: projectObjectiveId || "",
  });
  // The API returns an array of project qual steps with Id, Name, and related info
  if (response && Array.isArray(response)) {
    if (projectObjectiveId) {
      return response.map((step) => {
        return {
          Id: step?.Id,
          Name: step?.Name,
          ProjectName: step.Project__r?.Name || "",
          QualStepName: step.Qualification_Step__r?.Name || "",
          Funnel: step?.Funnel__c || "",
          _projectQualStepId: step?.Id || "",
          _qualificationStepId: step?.Qualification_Step__c || "",
        };
      });
    } else {
      return response
        .filter((step) => {
          return !step.Project_Objective__c;
        })
        .map((step) => {
          return {
            Id: step?.Id,
            Name: step?.Name,
            ProjectName: step.Project__r?.Name || "",
            QualStepName: step.Qualification_Step__r?.Name || "",
            Funnel: step?.Funnel__c || "",
            _projectQualStepId: step?.Id || "",
            _qualificationStepId: step?.Qualification_Step__c || "",
          };
        });
    }
  }
  return [];
}

export async function searchQualStepsByName(searchKey) {
  const response = await sfGet("/ProjectQualStep", {
    searchkey: searchKey,
  });
  // The API returns an array of project qual steps with Id, Name, and related info
  if (response && Array.isArray(response)) {
    return response.map((step) => {
      // Prefer the underlying Qualification Step Id for lookups
      const qualStepId =
        step?.Qualification_Step__c || step?.QualificationStepId || null;
      const name = step?.Qualification_Step__r?.Name || step?.Name;
      return {
        Id: qualStepId || step?.Id,
        Name: name,
        ProjectName: step?.Project__r?.Name || "",
        QualStepName: step?.Qualification_Step__r?.Name || "",
        Funnel: step?.Funnel__c || "",
        _projectQualStepId: step?.Id,
        _qualificationStepId: qualStepId,
      };
    });
  }
  return [];
}

// Example helper for a projects endpoint (adjust path as needed):
// export function fetchProjectById(projectId) {
//   return sfGet(`/projects/${projectId}`);
// }
